import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { FunctionComponent, useMemo } from 'react';
import TranslatedLink from './TranslatedLink';
import Script from 'next/script';
import { useLocale } from 'hooks/useLocale';

export type BreadcrumbItem = {
  label: string;
  link: string;
  isTranslatable: boolean;
};

interface BBreadcrumbProps {
  breadcrumbItems: BreadcrumbItem[];
  wrapperClasses?: string;
}

const BBreadcrumb: FunctionComponent<BBreadcrumbProps> = ({
  breadcrumbItems,
  wrapperClasses,
}) => {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const isCurrent = (idx: number) => {
    return idx === breadcrumbItems.length - 1;
  };

  const breadcrumbSchema = useMemo(() => {
    return `{"@context": "https://schema.org","@type": "BreadcrumbList","itemListElement": [${breadcrumbItems
      .map((item, idx) => {
        const label = item.isTranslatable ? t(item.label) : item.label;
        const nonQPLink = item.link.split('?')[0];
        const prefixedLink = `https://www.teknevia.com${nonQPLink}`;
        return `{"@type": "ListItem","position":${
          +idx + 1
        },"name":"${label}","item":"${prefixedLink}"}`;
      })
      .join(',')}]}`;
  }, [breadcrumbItems, t, locale]);

  return (
    <nav aria-label="breadcrumbs">
      <Script
        id="breadcrumb-schema"
        type="application/ld+json"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          __html: breadcrumbSchema,
        }}
      />
      <ul
        className={classNames(
          wrapperClasses,
          'flex flex-wrap items-center gap-2'
        )}
      >
        {breadcrumbItems.map((item, idx) => (
          <li
            key={item.link}
            className={classNames('breadcrumb-item text-xs md:text-sm', {
              'text-primary-600 underline': !isCurrent(idx),
              'text-black': isCurrent(idx),
            })}
          >
            {isCurrent(idx) ? (
              item.isTranslatable ? (
                t(item.label)
              ) : (
                item.label
              )
            ) : (
              <TranslatedLink href={item.link}>
                <a>{item.isTranslatable ? t(item.label) : item.label}</a>
              </TranslatedLink>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default BBreadcrumb;
